/**
 * PresetChips — clickable preset value chip row.
 * Single responsibility: quick-select a preset value from a list of chips.
 */

import React from 'react';

export interface PresetChip<T> {
  label: string;
  value: T;
  title?: string;
}

export interface PresetChipsProps<T> {
  label?: string;
  chips: PresetChip<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  isDark: boolean;
}

/**
 * PresetChips component — flex-wrapped row of small toggle buttons.
 */
export const PresetChips = <T,>({
  label,
  chips,
  value,
  onChange,
  isDark,
}: PresetChipsProps<T>): React.ReactElement => {
  const accent = isDark ? '#22c55e' : '#16a34a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const inactiveBg = isDark ? 'rgba(15,23,42,0.4)' : '#f8fafc';

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
  };

  const chipsRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  };

  return (
    <div style={containerStyle}>
      {label && <span style={labelStyle}>{label}</span>}
      <div style={chipsRowStyle}>
        {chips.map((chip, idx) => {
          const isActive = chip.value === value;
          const chipBtnStyle: React.CSSProperties = {
            padding: '3px 8px',
            borderRadius: 4,
            border: `1px solid ${isActive ? accent : borderColor}`,
            background: isActive ? accent : inactiveBg,
            color: isActive ? '#ffffff' : textPrimary,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: isActive ? 600 : 400,
            lineHeight: 1.4,
            transition: 'background 0.15s, color 0.15s, border-color 0.15s',
          };

          return (
            <button
              key={idx}
              type="button"
              style={chipBtnStyle}
              onClick={() => onChange(chip.value)}
              aria-pressed={isActive}
              aria-label={chip.title ?? chip.label}
              title={chip.title ?? chip.label}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

(PresetChips as React.FC).displayName = 'PresetChips';
